import { all, takeLeading, put, call, select, takeEvery } from 'redux-saga/effects';
import { api, apiCall, commonApiSaga } from 'redux/helpers/api';
import { snackbarActions } from 'redux/snackbar';
import history from 'providers/history';
import { workspaceActions } from 'redux/workspace';
import { challengeActions } from 'redux/challenge';
import { isMobile } from 'helpers';
import { debouncePerType, takeEveryAndAggregate, takeLeadingPerKey } from 'redux/helpers/saga';
import { channelSelectors } from 'redux/channel';
import uniq from 'lodash/uniq';
import { authSelectors } from 'redux/auth';
import config from 'config';
import types from './types';
import selectors from './selectors';
import actions from './actions';

function* onAddLike({ payload }) {
  const { ok, error } = yield apiCall(api.post.addLike, payload);

  if (ok) {
    yield put(actions.addLikeSuccess(payload));
    yield put(actions.get({ postId: payload.postId }));
  } else {
    yield put(actions.addLikeFailure(error));
  }
}

function* onRemoveLike({ payload }) {
  const { ok, error } = yield apiCall(api.post.removeLike, payload);

  if (ok) {
    yield put(actions.removeLikeSuccess(payload));
    yield put(actions.get({ postId: payload.postId }));
  } else {
    yield put(actions.removeLikeFailure(error));
  }
}

function* onGet({ payload, config: actionConfig }) {
  const { ok, response, error } = yield apiCall(api.post.get, payload, actionConfig);

  if (ok) {
    yield put(actions.getSuccess(response));
  } else {
    yield put(actions.getFailure({ postId: payload.postId, ...error }));
  }
}

function* onStore({ payload, config: actionConfig }) {
  const { ok, response, error } = yield apiCall(api.post.store, payload, actionConfig);

  if (ok) {
    yield put(actions.storeSuccess(response));

    const post = yield select(state => selectors.getPost(state, response.data));

    const channelSelector = yield select(channelSelectors.getChannelSelector);
    const channel = yield channelSelector(post ? post.channel : null);
    const originType = channel ? channel.originType : null;

    const location = isMobile ? window.location.hash : window.location.pathname;
    const challengeId = (/^#?\/challenges\/(\d+)\/?$/.exec(location) || /^#?\/activity\/create\?(?:.*&)?challengeId=(\d+)/.exec(location) || [])[1];

    // A full-screen create form shouldn't stay in history behind what it created
    const fromCreatePage = /^#?\/(post|activity)\/create(\?|$)/.test(location);
    const navigate = fromCreatePage ? history.replace : history.push;

    if (challengeId) {
      yield put(workspaceActions.getChallenges({ filter: 'active' }));
      yield put(challengeActions.getChallenge({ challengeId }));
    }

    if (originType === 'WORKSPACE') {
      yield call(navigate, `/messages/${channel.id}`);
    } else if (originType === 'GROUP') {
      yield call(navigate, `/groups/${channel.originId}`);
    } else if (originType === 'CHALLENGE') {
      yield call(navigate, `/challenges/${channel.originId}`);
    } else if (originType === 'USER' && challengeId && fromCreatePage) {
      // Opened from the challenge's "Add entry", so stepping back returns there
      yield call(history.goBack);
    } else if (originType === 'USER' && challengeId) {
      yield call(navigate, `/challenges/${challengeId}`);
    } else {
      yield call(navigate, `/post/${post.id}`);
    }
  } else {
    yield put(actions.storeFailure(error));
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onUpdate({ payload, config: actionConfig }) {

  // Formdata can't send null/undefined values and these get removed in our toFormData function
  // At the moment I'm unsure, if this logic can be added globally to the toFormData function
  const scheduledAt = payload.scheduledAt === null ? '' : payload.scheduledAt;
  const poll = payload.poll && payload.poll.closeAt === null
    ? { ...payload.poll, closeAt: '' }
    : payload.poll;

  const { ok, response, error } = yield apiCall(api.post.update, { ...payload, scheduledAt, poll }, actionConfig);

  if (ok) {
    response.postId = payload.postId;
    yield put(actions.updateSuccess(response));
    yield put(snackbarActions.createSuccess('post.updated'));
    yield call(history.replace, `/post/${payload.postId}`);
  } else {
    yield put(actions.updateFailure(error));
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onDelete({ payload, config: actionConfig }) {
  const { ok, error } = yield apiCall(api.post.delete, payload, actionConfig);

  if (ok) {
    yield put(actions.deleteSuccess({ postId: payload.postId }));
    yield put(snackbarActions.createSuccess('post.deleted'));
  } else {
    yield put(actions.deleteFailure(error));
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onCopy({ payload, config: actionConfig }) {
  const { ok, response, error } = yield apiCall(api.post.copy, payload, actionConfig);

  if (!ok) {
    yield put(actions.copyFailure(error));
    yield put(snackbarActions.createFailure(error.message));

    return;
  }

  yield put(actions.copySuccess(response));

  const copyId = response.data;

  if (isMobile) {
    yield call(history.push, `/post/${copyId}/edit`);
  } else {
    yield call(history.push, `/post/${copyId}`, { editPostId: copyId });
  }
}

function* onShareToNetworkSuccess({ payload }) {
  const added = payload?.data?.recipientsAdded ?? 0;
  const total = payload?.data?.recipientsTotal ?? 0;
  const wasSharedBefore = total > added;

  const message = wasSharedBefore ? 'post.resharedToNetwork' : 'post.sharedToNetwork';

  yield put(snackbarActions.createSuccess(message, { values: { count: added, total } }));
}

function* onAddComment({ payload, config: actionConfig }) {
  const data = yield apiCall(api.post.addComment, payload, actionConfig);

  if (data.ok) {
    data.response.postId = payload.postId;
    yield put(actions.addCommentSuccess(data.response));
    yield put(actions.get({ postId: payload.postId }));
  } else {
    yield put(actions.addCommentFailure(data.error));
    yield put(snackbarActions.createFailure(data.error.message));
  }
}

function* onGetComments({ payload }) {
  const { ok, response, error } = yield apiCall(api.post.getComments, payload);

  if (ok) {
    response.postId = payload.postId;
    yield put(actions.getCommentsSuccess(response));
  } else {
    yield put(actions.getCommentsFailure(error));
  }
}

function* onGetLikes({ payload }) {
  const { ok, response, error } = yield apiCall(api.post.getLikes, payload);

  if (ok) {
    response.postId = payload.postId;
    yield put(actions.getLikesSuccess(response));
  } else {
    yield put(actions.getLikesFailure(error));
  }
}

function* onGetSmsExpenseEstimations({ payload }) {
  const { ok, response, error } = yield apiCall(api.post.getSmsExpenseEstimation, payload);
  if (ok) {
    yield put(actions.getSmsExpenseEstimationSuccess(response));
  } else {
    yield put(actions.getSmsExpenseEstimationFailure(error));
  }
}

function* onGetTranslations({ payload }) {
  const { ok, response, error } = yield apiCall(api.post.getTranslations, payload);

  if (ok) {
    response.postId = payload.postId;
    yield put(actions.getTranslationsSuccess(response));
  } else {
    yield put(actions.getTranslationsFailure(error));
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onPatch({ payload, config: actionConfig }) {
  const { ok, response, error } = yield apiCall(api.post.patch, payload, actionConfig);

  if (ok) {
    yield put(actions.patchSuccess({ pinned: payload.pinned, ...response }));
  } else {
    yield put(actions.patchFailure(error));
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onFollow({ payload }) {
  const { ok, response, error } = yield apiCall(api.post.follow, payload);

  if (ok) {
    yield put(actions.followSuccess({ pinned: payload.pinned, ...response }));
  } else {
    yield put(actions.followFailure(error));
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onUnfollow({ payload }) {
  const { ok, response, error } = yield apiCall(api.post.unfollow, payload);

  if (ok) {
    yield put(actions.unfollowSuccess({ pinned: payload.pinned, ...response }));
  } else {
    yield put(actions.unfollowFailure(error));
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onGetFeed({ payload: { feedName, feedType, feedId, ...payload } }) {
  const workspaceId = yield select(authSelectors.getWorkspaceId);

  // TODO: Don't trigger without workspaceId

  let apiResponse = null;

  switch (feedType) {
    case 'home':
      apiResponse = yield apiCall(api.workspace.homeFeed, { ...payload, workspaceId }); break;
    case 'challenges':
      apiResponse = yield apiCall(api.challenge.getChallengesFeed, { ...payload, workspaceId }); break;
    case 'groups':
      apiResponse = yield apiCall(api.workspace.groupsFeed, { ...payload, workspaceId }); break;
    case 'group':
      apiResponse = yield apiCall(api.group.getPosts, { ...payload, groupId: feedId }); break;
    case 'record':
      apiResponse = yield apiCall(api.drive.getRecordFeed, { ...payload, recordId: feedId }); break;
    case 'member':
      apiResponse = yield apiCall(api.member.getMemberFeed, { ...payload, memberId: feedId }); break;
    case 'challenge':
      apiResponse = yield apiCall(api.challenge.getChallengeFeed, { ...payload, challengeId: feedId }); break;
    case 'event':
      apiResponse = yield apiCall(api.calendar.getEventFeed, { ...payload, eventId: feedId }); break;
    case 'channel':
      apiResponse = yield apiCall(api.channel.getFeed, { ...payload, channelId: feedId }); break;
    case 'course':
      apiResponse = yield apiCall(api.training.getCourseFeed, { ...payload, courseId: feedId }); break;
    default:
      apiResponse = { error: true };
      break;
  }

  const { ok, response, error } = apiResponse;

  if (ok) {
    yield put(actions.getFeedSuccess({ feedName, ...response }));
  } else {
    yield put(actions.getFeedFailure(error));
  }
}

const getReachPercentageActionAggregator = (actionsArray) => ({
  postIds: uniq(actionsArray.map(({ payload }) => payload?.postId).filter(v => !!v)),
});

export default function* postSagas() {
  yield all([
    takeLeading(types.get, onGet),
    takeLeading(types.store, onStore),
    takeLeading(types.update, onUpdate),
    takeLeading(types.delete, onDelete),
    takeLeadingPerKey(types.copy, onCopy, ({ payload }) => payload.postId),
    takeLeadingPerKey(types.shareToNetwork, commonApiSaga, ({ payload }) => payload.postId, { apiFn: api.post.shareToNetwork }),
    takeEvery(types.shareToNetworkSuccess, onShareToNetworkSuccess),
    takeLeading(types.follow, onFollow),
    takeLeading(types.unfollow, onUnfollow),
    takeLeading(types.addComment, onAddComment),
    takeLeading(types.addLike, onAddLike),
    takeLeading(types.removeLike, onRemoveLike),
    takeLeadingPerKey(types.getComments, onGetComments, ({ payload }) => payload.postId),
    takeLeadingPerKey(types.getLikes, onGetLikes, ({ payload }) => payload.postId),
    takeLeadingPerKey(types.patch, onPatch, ({ payload }) => payload.postId),
    debouncePerType(config.defaultTypingDebounce, types.getSmsExpenseEstimation, onGetSmsExpenseEstimations),
    takeLeading(types.getTranslations, onGetTranslations),

    takeEvery(types.getReachPercentages, commonApiSaga, { apiFn: api.post.getReachPercentages }),

    takeLeadingPerKey(types.getFeed, onGetFeed, ({ payload }) => payload.feedName),

    takeEveryAndAggregate(types.getReachPercentage, getReachPercentageActionAggregator, actions.getReachPercentages),
  ]);
}
